// src/ui/PollBubble.jsx — the poll card in the conversation.
//
// Votes are optimistic: the bar moves the instant you click, then the Firestore
// transaction confirms (or the snapshot corrects it). Single-choice polls move your
// vote between options exactly the way the phone's _applyDelta does, so the two never
// disagree about what you picked.
import React, { useMemo, useState } from 'react';
import { Poll, Check } from './icons';
import { readablePoll, castVote } from '../services/polls';

export default function PollBubble({ m, mine, chat, myUid, myName }) {
  const [busy, setBusy] = useState(null);      // optionId being written
  const [optimistic, setOptimistic] = useState(null);
  const [err, setErr] = useState('');

  const poll = useMemo(() => readablePoll(m), [m]);
  const options = optimistic?.options || poll.options;
  const totalVotes = optimistic?.totalVotes ?? (m.totalVotes || 0);
  const voters = optimistic?.votedUsers ?? (m.votedUsers || []);
  const iVoted = voters.includes(myUid);

  if (poll.locked) {
    return (
      <div className="poll">
        <div className="poll-q locked">🔒 Encrypted poll</div>
        <div className="poll-sub">This poll was created before this browser was linked.</div>
      </div>
    );
  }

  const vote = async (opt) => {
    if (busy) return;
    setBusy(opt.id); setErr('');
    // paint it immediately — a poll that waits on a round-trip feels broken
    const prev = { options, totalVotes, votedUsers: voters };
    try {
      const res = await castVote({
        chat, myUid, myName, message: { ...m, options }, optionId: opt.id, optionText: opt.text,
      });
      setOptimistic({
        options: res.options.map((o) => ({ ...o, text: options.find((x) => x.id === o.id)?.text || '' })),
        totalVotes: res.totalVotes,
        votedUsers: res.votedUsers,
      });
    } catch (e) {
      setOptimistic(prev);
      setErr(e?.message || "Couldn't record that vote.");
    } finally {
      setBusy(null);
    }
  };

  const max = Math.max(1, ...options.map((o) => o.votes?.length || 0));

  return (
    <div className={`poll ${mine ? 'out' : 'in'}`}>
      <div className="poll-head">
        <Poll width={15} height={15} />
        <span>{poll.multipleChoice ? 'Select one or more' : 'Select one'}</span>
      </div>

      <div className="poll-q">{poll.question}</div>

      <div className="poll-opts">
        {options.map((o) => {
          const count = o.votes?.length || 0;
          const chosen = (o.votes || []).includes(myUid);
          // share of VOTERS, so a multi-choice poll can't read as >100%
          const pct = totalVotes > 0 ? Math.round((count / Math.max(1, max)) * 100) : 0;
          return (
            <button
              key={o.id}
              className={`poll-opt ${chosen ? 'chosen' : ''} ${busy === o.id ? 'busy' : ''}`}
              onClick={() => vote(o)}
              disabled={!!busy}
            >
              <span className="poll-fill" style={{ width: `${iVoted ? pct : 0}%` }} />
              <span className="poll-mark">{chosen ? <Check width={13} height={13} /> : null}</span>
              <span className="poll-text">{o.text}</span>
              {iVoted && <span className="poll-count">{count}</span>}
            </button>
          );
        })}
      </div>

      <div className="poll-foot">
        <span>{totalVotes === 0 ? 'No votes yet' : `${totalVotes} vote${totalVotes === 1 ? '' : 's'}`}</span>
        {!iVoted && <span className="poll-tip">Tap an option to vote</span>}
      </div>
      {err ? <div className="poll-err">{err}</div> : null}
    </div>
  );
}
